import React, { useEffect, useState } from 'react'
import { Dialog, CircularProgress, LinearProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CheckIcon from '@mui/icons-material/Check'
import { DetailButton } from '../common/detail'
import { AnalyzeProofResponse } from '../../types/maturityScoringTypes'
import '../common/detail/detail.css'
import './AiMaturitySuggestionModal.css'
import { formatDecimal } from '../../utils/formatNumbers'

type AiMaturitySuggestionModalProps = {
  open: boolean
  phase: 'choice' | 'analyzing' | 'ready' | 'error'
  result: AnalyzeProofResponse | null
  userScore: number
  suggestedScore: number
  errorMessage?: string | null
  onClose: () => void
  onAccept: () => void
  onKeep: () => void
  onUseAi?: () => void
  onSaveOnly?: () => void
  onRetry?: () => void
  onCancelAnalysis?: () => void
  t: (key: string, options?: Record<string, unknown>) => string
}

const WAIT_STEP_KEYS = [
  'stepUpload',
  'stepRead',
  'stepMap',
  'stepScore',
  'stepFinalize',
] as const

const WAIT_TIP_KEYS = [
  'tip1',
  'tip2',
  'tip3',
  'tip4',
] as const

function formatElapsed(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const AiMaturitySuggestionModal: React.FC<AiMaturitySuggestionModalProps> = ({
  open,
  phase,
  result,
  userScore,
  suggestedScore,
  errorMessage,
  onClose,
  onAccept,
  onKeep,
  onUseAi,
  onSaveOnly,
  onRetry,
  onCancelAnalysis,
  t,
}) => {
  const [stepIndex, setStepIndex] = useState(0)
  const [tipIndex, setTipIndex] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!open || phase !== 'analyzing') {
      setStepIndex(0)
      setTipIndex(0)
      setElapsed(0)
      return
    }

    const elapsedTimer = window.setInterval(() => {
      setElapsed((s) => s + 1)
    }, 1000)

    // Advance visual steps slowly — analysis can take many minutes
    const stepTimer = window.setInterval(() => {
      setStepIndex((i) => Math.min(i + 1, WAIT_STEP_KEYS.length - 1))
    }, 45000)

    const tipTimer = window.setInterval(() => {
      setTipIndex((i) => (i + 1) % WAIT_TIP_KEYS.length)
    }, 8000)

    return () => {
      window.clearInterval(elapsedTimer)
      window.clearInterval(stepTimer)
      window.clearInterval(tipTimer)
    }
  }, [open, phase])

  const conformity =
    typeof result?.conformityScore === 'number'
      ? formatDecimal(result.conformityScore)
      : null

  const tipKey = WAIT_TIP_KEYS[tipIndex]
  const progressPct = Math.min(
    92,
    8 + stepIndex * 18 + Math.min(elapsed, 60) * 0.35
  )

  return (
    <Dialog
      open={open}
      onClose={phase === 'analyzing' ? onCancelAnalysis : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={phase === 'analyzing' && !onCancelAnalysis}
      PaperProps={{ className: 'incyia-modal-paper ai-msm-paper' }}
    >
      <div className="ai-msm">
        <div className="ai-msm-glow" aria-hidden />

        <div className="ai-msm-head">
          <div className="ai-msm-head-copy">
            <div className="ai-msm-badge">
              <AutoAwesomeIcon sx={{ fontSize: 14 }} />
              {t('popups.aiMaturitySuggestion.badge', {
                defaultValue: 'Powered by INCYIA AI',
              })}
            </div>
            <h2 className="ai-msm-title">
              {phase === 'choice'
                ? t('popups.aiMaturitySuggestion.choiceTitle')
                : phase === 'analyzing'
                ? t('popups.aiMaturitySuggestion.analyzingTitle', {
                    defaultValue: 'Analyzing your proof…',
                  })
                : phase === 'error'
                  ? t('popups.aiMaturitySuggestion.errorTitle', {
                      defaultValue: 'AI suggestion unavailable',
                    })
                  : t('popups.aiMaturitySuggestion.title', {
                      defaultValue: 'AI score suggestion',
                    })}
            </h2>
            <p className="ai-msm-sub">
              {phase === 'choice'
                ? t('popups.aiMaturitySuggestion.choiceSubtitle')
                : phase === 'analyzing'
                ? t('popups.aiMaturitySuggestion.analyzingSubtitle', {
                    defaultValue:
                      'Our AI is carefully reading your evidence. This usually takes 30–90 seconds — please keep this window open.',
                  })
                : phase === 'error'
                  ? t('popups.aiMaturitySuggestion.errorSubtitle', {
                      defaultValue:
                        'We could not analyze the proof right now. You can continue with your own score.',
                    })
                  : t('popups.aiMaturitySuggestion.subtitle', {
                      defaultValue:
                        'Based on your uploaded proof, AI proposes a maturity score. Accept it to update your answer, or keep the score you set.',
                    })}
            </p>
          </div>
          {phase !== 'analyzing' || onCancelAnalysis ? (
            <button
              type="button"
              className="detail-icon-btn"
              onClick={phase === 'analyzing' ? onCancelAnalysis : onClose}
              aria-label={t('popups.addMaturityScoring.cancel')}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>

        {phase === 'analyzing' ? (
          <div className="ai-msm-analyzing">
            <div className="ai-msm-orb">
              <CircularProgress
                size={40}
                thickness={3.5}
                sx={{ color: '#0D9B87' }}
              />
            </div>

            <p className="ai-msm-elapsed" aria-live="polite">
              {t('popups.aiMaturitySuggestion.elapsed', {
                time: formatElapsed(elapsed),
                defaultValue: `Elapsed {{time}} · please wait`,
              })}
            </p>

            <div className="ai-msm-progress">
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  backgroundColor: '#E8EEF0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 99,
                    background:
                      'linear-gradient(90deg, #0D9B87 0%, #076B5C 100%)',
                  },
                }}
              />
              <span className="ai-msm-progress-label">
                {t('popups.aiMaturitySuggestion.progressHint', {
                  defaultValue: 'Deep analysis in progress…',
                })}
              </span>
            </div>

            <ol className="ai-msm-wait-steps">
              {WAIT_STEP_KEYS.map((key, index) => {
                const done = index < stepIndex
                const active = index === stepIndex
                return (
                  <li
                    key={key}
                    className={`ai-msm-wait-step${done ? ' is-done' : ''}${active ? ' is-active' : ''}`}
                  >
                    <span className="ai-msm-wait-ico" aria-hidden>
                      {done ? (
                        <CheckIcon sx={{ fontSize: 14 }} />
                      ) : active ? (
                        <span className="ai-msm-wait-dot" />
                      ) : (
                        <span className="ai-msm-wait-num">{index + 1}</span>
                      )}
                    </span>
                    <span>
                      {t(`popups.aiMaturitySuggestion.${key}`, {
                        defaultValue: key,
                      })}
                    </span>
                  </li>
                )
              })}
            </ol>

            <div className="ai-msm-tip" key={tipKey}>
              <AutoAwesomeIcon sx={{ fontSize: 16, color: '#0D9B87' }} />
              <p>
                {t(`popups.aiMaturitySuggestion.${tipKey}`, {
                  defaultValue:
                    'Larger documents take longer — thank you for your patience.',
                })}
              </p>
            </div>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className="ai-msm-error">
            <p>
              {errorMessage ||
                t('popups.aiMaturitySuggestion.errorGeneric', {
                  defaultValue: 'Analysis failed. Please try again later.',
                })}
            </p>
            <div className="ai-msm-foot">
              {onRetry ? (
                <DetailButton variant="ghost" onClick={onRetry}>
                  {t('popups.aiMaturitySuggestion.retry')}
                </DetailButton>
              ) : null}
              <DetailButton onClick={onKeep}>
                {t('popups.aiMaturitySuggestion.continueAnyway', {
                  defaultValue: 'Continue with my score',
                })}
              </DetailButton>
            </div>
          </div>
        ) : null}

        {phase === 'choice' ? (
          <div className="ai-msm-foot">
            <DetailButton variant="ghost" onClick={onSaveOnly}>
              {t('popups.aiMaturitySuggestion.saveScoreOnly')}
            </DetailButton>
            <DetailButton onClick={onUseAi}>
              {t('popups.aiMaturitySuggestion.useAi')}
            </DetailButton>
          </div>
        ) : null}

        {phase === 'analyzing' && onCancelAnalysis ? (
          <div className="ai-msm-foot">
            <DetailButton variant="ghost" onClick={onCancelAnalysis}>
              {t('popups.aiMaturitySuggestion.cancelAnalysis')}
            </DetailButton>
          </div>
        ) : null}

        {phase === 'ready' && result ? (
          <>
            <div className="ai-msm-score-panel">
              <div className="ai-msm-score-main">
                <span className="ai-msm-score-label">
                  {t('popups.aiMaturitySuggestion.suggestedLabel', {
                    defaultValue: 'Suggested score',
                  })}
                </span>
                <div className="ai-msm-score-row">
                  <span className="ai-msm-score-num">
                    {formatDecimal(suggestedScore)}
                  </span>
                </div>
              </div>

              <div className="ai-msm-stats">
                {conformity != null ? (
                  <div className="ai-msm-stat">
                    <strong>{conformity}%</strong>
                    <span>
                      {t('popups.aiMaturitySuggestion.conformity', {
                        defaultValue: 'Conformity',
                      })}
                    </span>
                  </div>
                ) : null}
                <div className="ai-msm-stat">
                  <strong>{result.clausesEvaluated ?? '—'}</strong>
                  <span>
                    {t('popups.aiMaturitySuggestion.clauses', {
                      defaultValue: 'Clauses evaluated',
                    })}
                  </span>
                </div>
                <div className="ai-msm-stat">
                  <strong>{formatDecimal(userScore)}</strong>
                  <span>
                    {t('popups.aiMaturitySuggestion.yourScore', {
                      defaultValue: 'Your score',
                    })}
                  </span>
                </div>
                {typeof result.targetScore === 'number' ? (
                  <div className="ai-msm-stat">
                    <strong>{formatDecimal(result.targetScore)}</strong>
                    <span>
                      {t('popups.aiMaturitySuggestion.targetScore', {
                        defaultValue: 'Target score',
                      })}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            {result.exceedsTargetScore ? (
              <p className="ai-msm-cap-warn">
                {t('popups.aiMaturitySuggestion.cappedHint', {
                  suggested: formatDecimal(Number(result.suggestedScore)),
                  allowed: formatDecimal(suggestedScore),
                  defaultValue: `AI suggested ${formatDecimal(Number(result.suggestedScore))}, capped to ${formatDecimal(suggestedScore)} by the subdomain target.`,
                })}
              </p>
            ) : null}

            {(result.analysis?.document_type ||
              result.analysis?.subdomain ||
              result.analysis?.language) && (
              <div className="ai-msm-meta">
                {result.analysis.document_type ? (
                  <span>
                    {t('popups.aiMaturitySuggestion.docType', {
                      defaultValue: 'Document',
                    })}
                    : <strong>{result.analysis.document_type}</strong>
                  </span>
                ) : null}
                {result.analysis.subdomain ? (
                  <span>
                    {t('popups.aiMaturitySuggestion.subdomain', {
                      defaultValue: 'Subdomain',
                    })}
                    : <strong>{result.analysis.subdomain}</strong>
                  </span>
                ) : null}
                {result.analysis.language ? (
                  <span>
                    {t('popups.aiMaturitySuggestion.language', {
                      defaultValue: 'Language',
                    })}
                    : <strong>{result.analysis.language}</strong>
                  </span>
                ) : null}
              </div>
            )}

            <div className="ai-msm-hint">
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#0D9B87' }} />
              <p>
                {t('popups.aiMaturitySuggestion.acceptHint', {
                  defaultValue:
                    'Accepting applies the AI score to your answer field, then saves. Declining keeps your score.',
                })}
              </p>
            </div>

            <div className="ai-msm-foot">
              <DetailButton variant="ghost" onClick={onKeep}>
                {t('popups.aiMaturitySuggestion.keepMine', {
                  defaultValue: 'Keep my score',
                })}
              </DetailButton>
              <DetailButton onClick={onAccept}>
                {t('popups.aiMaturitySuggestion.accept', {
                  defaultValue: 'Accept AI score',
                })}
              </DetailButton>
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  )
}

export default AiMaturitySuggestionModal
